import React from 'react';
import { proxy } from 'valtio';

export const defaultDelay = 0;

export const isDefaultMessage = (message: string | readonly []) => message === '';

export interface SnackbarMessageProps {
  linkButton(
    onClick: (event: React.MouseEvent<HTMLElement>) => void,
    linkMessage: string
  ): React.ReactElement;
}

interface MessageOptions {
  timeout?: number;
  delay?: number;
  messageData?: any;
  showCloseButton?: boolean;
  i18nVariables?: Record<string, string | number>;
  progress?: number;
  onDismiss?: () => void;
}

interface SnackbarState {
  message: string | [];
  messageData?: string;
  /**
   * A number between 0 and 1 representing the progress of the snackbar.
   * Note: If you want to reset the loading bar without a transition, do not
   * define progress in the next setMessage(), or set this to undefined.
   */
  progress?: number;
  delay: number;
  timeout?: NodeJS.Timeout;
  delayTimeout?: NodeJS.Timeout;
  showCloseButton?: boolean;
  i18nVariables: Record<string, string | number>;
  clearMessage(message?: string): void;
  setProgress(progress: number): void;
  setMessage(message: string, options?: MessageOptions): void;
  onClose(): void;
  onDismiss(): void;
}

export const getMessage = (
  message: string | readonly [],
  actions?: any
): string | React.FC<SnackbarMessageProps> | readonly [] => {
  if (!actions || actions.length === 0 || message === '') {
    return message;
  }

  return function SnackbarMessage({ linkButton }) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', maxWidth: '560px' }}>
        <span>{message}</span> &nbsp;
        {linkButton(actions[0].onClick, actions[0].content)}
      </div>
    );
  };
};

export const currentSnackbarState = proxy<SnackbarState>({
  message: '',
  delay: defaultDelay,
  delayTimeout: undefined,
  i18nVariables: {},
  onDismiss: () => undefined,
  progress: undefined,

  clearMessage(message) {
    if (message && message !== currentSnackbarState.message) {
      return;
    }

    if (currentSnackbarState.timeout) {
      clearTimeout(currentSnackbarState.timeout);
    }

    currentSnackbarState.message = '';
    currentSnackbarState.i18nVariables = {};
    currentSnackbarState.delay = defaultDelay;
  },

  setProgress(progress: number) {
    currentSnackbarState.progress = progress;
  },

  setMessage(message, options) {
    if (options?.i18nVariables) {
      currentSnackbarState.i18nVariables = options.i18nVariables;
    }
    if (typeof options?.delay !== 'number') {
      handleSnackbarUpdate(message, options);
    } else {
      currentSnackbarState.delayTimeout = setTimeout(
        () => handleSnackbarUpdate(message, options),
        options.delay
      );
    }
  },

  onClose() {
    if (currentSnackbarState.timeout) {
      clearTimeout(currentSnackbarState.timeout);
    }
    if (currentSnackbarState.delayTimeout) {
      clearTimeout(currentSnackbarState.delayTimeout);
    }
    currentSnackbarState.delay = 0;
    currentSnackbarState.message = '';
    currentSnackbarState.i18nVariables = {};
    currentSnackbarState.progress = undefined;

    currentSnackbarState.onDismiss();
  },
});

function handleSnackbarUpdate(message: string | [], options?: MessageOptions) {
  if (options?.progress || options?.progress === 0) {
    currentSnackbarState.progress = options.progress;
  } else {
    currentSnackbarState.progress = undefined;
  }

  if (currentSnackbarState.timeout) {
    clearTimeout(currentSnackbarState.timeout);
  }

  if (currentSnackbarState.delayTimeout) {
    clearTimeout(currentSnackbarState.delayTimeout);
  }

  currentSnackbarState.message = message;
  currentSnackbarState.showCloseButton = !!options?.showCloseButton;

  if (options?.messageData) {
    currentSnackbarState.messageData = options.messageData;
  }

  if (options?.onDismiss) {
    currentSnackbarState.onDismiss = options.onDismiss;
  }

  if (typeof options?.timeout === 'number') {
    currentSnackbarState.timeout = setTimeout(() => {
      currentSnackbarState.message = '';
      currentSnackbarState.i18nVariables = {};
      currentSnackbarState.delay = defaultDelay;
      currentSnackbarState.progress = undefined;
    }, options.timeout);
  }
}
